import { Add } from '@app/ui/iconComponents';
import { COLORS, TYPOGRAPHY } from '@b2b/shared/consts';
import {
  Dispatch,
  FC,
  ReactElement,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { WA_LINK } from '../consts/vars';

type AccordionContext = {
  activeAccordion: string | undefined;
  setActiveAccordion: Dispatch<SetStateAction<string | undefined>>;
};

const AccordionContext = createContext<AccordionContext | null>(null);
AccordionContext.displayName = 'AccordionContext';

interface AccordionProviderProps {
  children?: ReactNode | ReactNode[];
}

const AccordionProvider: FC<AccordionProviderProps> = ({ children }) => {
  const [activeAccordion, setActiveAccordion] = useState<string | undefined>();

  const value = useMemo(() => ({ activeAccordion, setActiveAccordion }), [activeAccordion]);

  return <AccordionContext.Provider value={value}>{children}</AccordionContext.Provider>;
};

interface AccordionProps {
  id: string;
  title: string;
  text: string | ReactElement;
  className?: string;
  isCustom?: boolean;
}

const AccordionItem: FC<AccordionProps> = ({ id, title, text, className, isCustom }) => {
  const { activeAccordion, setActiveAccordion } = useContext(AccordionContext)!;

  const isActive = useMemo(() => activeAccordion === id, [id, activeAccordion]);

  const addLinkToText = (text: string) => {
    return text.replace(
      /Службу поддержки/g,
      `<a href="${WA_LINK}" target="_blank" style="color: ${COLORS.primary300};">Службу поддержки</a>`,
    );
  };

  return (
    <Root
      className={className}
      $isActive={isActive}
      $isCustom={isCustom}
    >
      <Head
        $isActive={isActive}
        $isCustom={isCustom}
        onClick={() => (isActive ? setActiveAccordion(undefined) : setActiveAccordion(id))}
      >
        <Title>{title}</Title>
        <IconWrapper
          $isActive={isActive}
          $isCustom={isCustom}
        >
          <Add
            color={isActive ? COLORS.white : undefined}
            width={isCustom ? 40 : 24}
            height={isCustom ? 40 : 24}
          />
        </IconWrapper>
      </Head>
      {isActive && (
        <HiddenContent>
          <Text
            $isCustom={isCustom}
            dangerouslySetInnerHTML={{ __html: addLinkToText(text as string) }}
          />
        </HiddenContent>
      )}
    </Root>
  );
};

const Root = styled.div<StyleProps>`
  border-radius: 8px;
  border: ${({ $isCustom }) => ($isCustom ? `2px solid ${COLORS.primary300}` : `1px solid${COLORS.grayscale100}`)};

  ${({ $isCustom, $isActive }) => {
    if ($isCustom && $isActive) {
      return css`
        background-color: ${COLORS.primary300};
      `;
    }
  }};
`;

interface StyleProps {
  $isActive?: boolean;
  $isCustom?: boolean;
}

const Head = styled.button<StyleProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${COLORS.grayscale700};
  width: 100%;
  text-align: left;

  ${({ $isCustom, $isActive }) =>
    $isCustom && $isActive
      ? css`
          color: ${COLORS.white};
        `
      : css`
          color: ${COLORS.grayscale700};
        `};

  ${({ $isCustom }) =>
    $isCustom
      ? css`
          ${TYPOGRAPHY.body1Bold14}
        `
      : css`
          ${TYPOGRAPHY.headline2Medium16};
        `};

  ${({ $isActive }) =>
    $isActive
      ? css`
          padding: 16px 16px 10px 16px;
        `
      : css`
          padding: 16px;
        `};
`;

const Title = styled.span`
  padding-right: 40px;
`;

const IconWrapper = styled.div<StyleProps>`
  ${({ $isActive }) =>
    $isActive &&
    css`
      transform: rotate(45deg);
    `};
`;

const HiddenContent = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 16px;
`;

const Text = styled.p<StyleProps>`
  ${TYPOGRAPHY.body1Regular14};
  color: ${({ $isCustom }) => ($isCustom ? `${COLORS.white}` : `${COLORS.grayscale600}`)};
`;

export { AccordionProvider, AccordionItem };
